import {
  type ApolloClient,
  type ApolloError,
  type NormalizedCacheObject,
  useApolloClient,
} from '@apollo/client';
import type { Language } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { useDispatch } from 'react-redux';

import { useQuery, useToastMutation } from 'src/modules/apollo';
import { getDataFromCache } from 'src/modules/apollo/apolloCache';
import { useAppConfig } from 'src/modules/appConfig';
import type { Maybe } from 'src/types';
import * as Schema from 'src/types/graphql';

import translations from './.vocab';
import {
  deleteResume,
  deleteResumeSuccess,
  deleteResumeFailure,
  updateDefault,
  updateDefaultFailure,
  updateDefaultSuccess,
} from './actionCreators';
import {
  generateDeleteResumeOptimisticResponse,
  generateSetDefaultResumeOptimisticResponse,
  updateCacheDeleteResume,
  updateCacheSetDefaultResume,
} from './apolloCache';
import type { Origin, Resume } from './types';

const findDefaultResumeId = (resumes: Resume[]) => {
  const defaultResumeList = resumes.filter(
    (resume: Resume) => resume.isDefault === true,
  );
  return defaultResumeList.length === 1 ? defaultResumeList[0].id : '';
};

export const useResumes = () => {
  const { language: languageCode } = useAppConfig();

  const { loading, error, data } = useQuery(Schema.GetResumesDocument, {
    variables: {
      languageCode,
    },
  });

  const resumes =
    !loading && !error && data?.viewer?.resumes ? data.viewer.resumes : [];

  return {
    resumes,
    defaultResumeId: findDefaultResumeId(resumes),
    loading,
    error,
  };
};

export const useResumePrivacyDisclaimers = () => {
  const { language: languageCode, zone, isUnifiedSite } = useAppConfig();

  const { loading, error, data } = useQuery(
    Schema.GetResumePrivacyDisclaimersDocument,
    {
      variables: {
        languageCode,
        zone,
        isUnifiedSite,
      },
    },
  );

  const resumePrivacyDisclaimers =
    !loading && !error && data ? data.resumePrivacyDisclaimers : null;

  return {
    resumePrivacyDisclaimers,
  };
};

export const getResumesFromCache = ({
  apolloClient,
  languageCode,
}: {
  apolloClient: ApolloClient<NormalizedCacheObject | Record<string, unknown>>;
  languageCode?: Language;
}) => {
  const resumesCacheData = getDataFromCache({
    cache: apolloClient.cache,
    query: Schema.GetResumesDocument,
    variables: { languageCode: languageCode || 'en' },
  });

  return {
    resumes: resumesCacheData?.viewer?.resumes || [],
  };
};

/** Returns a mock response for use with the apollo MockedProvider */
export const mockGetResumesRequest = (resumes: Maybe<Resume[]>) => ({
  request: {
    query: Schema.GetResumesDocument,
    variables: { languageCode: 'en' },
  },
  result: {
    data: {
      viewer: {
        _id: '1234',
        resumes: resumes || [],
      },
    },
  },
});

const key = 'resume';

export const useDeleteResume = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();

  const onMutate = (
    { resumeId }: Schema.DeleteResumeMutationVariables,
    mutationMetadata?: { origin: Origin },
  ) => {
    dispatch(deleteResume(resumeId.id, mutationMetadata?.origin));
  };

  const onCompleted = () => {
    dispatch(deleteResumeSuccess());
  };

  const onError = (error: ApolloError) => {
    dispatch(deleteResumeFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.DeleteResumeDocument, {
    key,
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Resumé deleted'),
    failToastMessage: t('Resumé could not be deleted'),
    update: updateCacheDeleteResume,
    optimisticResponse: generateDeleteResumeOptimisticResponse({
      apolloClient,
    }),
  });
};

export const useMakeDefaultResume = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();

  const onMutate = (_: any, mutationMetadata?: { origin: Origin }) => {
    dispatch(updateDefault(mutationMetadata?.origin));
  };

  const onCompleted = () => {
    dispatch(updateDefaultSuccess());
  };

  const onError = (error: ApolloError) => {
    dispatch(updateDefaultFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.SetDefaultResumeDocument, {
    key: 'default-resume', // Assigning unique key for default resume related operations, otherwise would interrupt other resume toasts.
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Default resumé updated'),
    failToastMessage: t('Default resumé could not be updated'),
    update: updateCacheSetDefaultResume,
    optimisticResponse: generateSetDefaultResumeOptimisticResponse({
      apolloClient,
    }),
  });
};

export function useFindNextDefaultResume() {
  // Assumes resumes from graph is ordered by default resume first, then newest to oldest
  const { resumes } = useResumes();

  const nextDefaultResume = resumes.find(
    (resume) =>
      !resume.isDefault &&
      resume.fileMetadata.virusScanStatus === Schema.VirusScanStatus.Ok,
  );

  return nextDefaultResume
    ? {
        resumeId: nextDefaultResume.id,
        resumeName: nextDefaultResume.fileMetadata.name,
      }
    : undefined;
}
